import React, { useState, useEffect, useRef, useMemo } from 'react'
import PlotlyComponent from 'react-plotly.js'
import type { PlotData, Layout, Config, ModeBarButtonAny } from 'plotly.js'
import CustomIcons from './CustomIcons'
import Modal from './Modal'
import UniversalLoader from './UniversalLoader'
import AIEditableContainer from './AIEditableContainer'
import OneLineBtnHStack from './OneLineBtnHStack'
import { StandardCheckbox } from './StandardInputs'
import { CheckboxContainer } from './styles/GraphContainer.styles'
import AIPlusBadge from './AIPlusBadge'

import { Tooltip, Whisper } from 'rsuite'
import {
  Container,
  HeaderContainer,
  HeaderTitle,
  Toolbox,
  GraphPlotArea,
  OuterContainer,
  AIHStack,
  AICheckboxAndContainer,
  WideModeContainer,
  WideModeOuterContainer,
  WideModeSplitter,
  ModalCheckboxContainer,
  OverwriteMessageContainer,
} from './styles/GraphContainer.styles'
import theme from '../../styles/theme'
import { SetBodyFunction } from '../reports/types/ReportBodyTypes'
import {
  type BaseSlice,
  AITextType,
} from '../reports/comp_reusable/report_page_universal/stores/baseReportStore'

// Type definitions

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

interface BaseConfigInterface {
  displayModeBar: boolean
  displaylogo: boolean
  modeBarButtons: ModeBarButtonAny[][]
  toImageButtonOptions: {
    format: 'png' | 'svg' | 'jpeg' | 'webp'
    filename: string
    height: number
    width: number
  }
}

interface HeaderButton {
  icon: React.ReactNode
  onClick: () => void
  ariaLabel: string
}

interface GraphContainerModalProps {
  title: string
  data?: PlotData[]
  layout?: Partial<Layout>
  onClose: () => void
  id?: string
  helpText?: string
  aiText?: string
  aiPlusText?: string
  AISettings?: any
  hasAIPlus?: boolean
  plotGroupId?: number
  plotPackageId?: number
  setReportDocument?: SetBodyFunction
  isNewlyGeneratedReport?: boolean
  useAIText?: boolean
  overwriteMessage?: React.ReactNode
  open?: boolean
  store?: BaseStoreHook<any>
  [key: string]: any
}

interface HelpModalProps {
  title: string
  helpText?: string
  open: boolean
  onClose: () => void
}

interface GraphContainerProps {
  title: string
  hasAIPlus?: boolean
  AISettings?: any
  helpText?: string
  aiText?: string
  aiPlusText?: string
  size?: 'small' | 'big'
  loading?: boolean
  data?: PlotData[]
  layout?: Partial<Layout>
  downloadLink?: string | null
  config?: Partial<Config>
  plotGroupId?: number
  plotPackageId?: number
  setReportDocument?: SetBodyFunction
  useAIText?: boolean
  isNewlyGeneratedReport?: boolean
  wide?: boolean
  overwriteMessage?: React.ReactNode
  store?: BaseStoreHook<any>
}

const baseConfig: BaseConfigInterface = {
  displayModeBar: true,
  displaylogo: false,
  modeBarButtons: [
    [
      'toImage' as ModeBarButtonAny,
      'pan2d' as ModeBarButtonAny,
      'zoomIn2d' as ModeBarButtonAny,
      'zoomOut2d' as ModeBarButtonAny,
      'autoScale2d' as ModeBarButtonAny,
    ],
  ],
  toImageButtonOptions: {
    format: 'png',
    filename: 'graph_export',
    height: 1080,
    width: 1920,
  },
}

const updateModebarPosition = (
  plotContainerRef: React.RefObject<HTMLDivElement | null>,
  toolboxRef: React.RefObject<HTMLDivElement | null>,
  offsetX: number,
  offsetY: number,
): void => {
  const modebar = plotContainerRef.current?.querySelector(
    '.modebar',
  ) as HTMLElement

  if (!modebar) return

  const modebarAnchor = toolboxRef.current?.querySelector(
    '.modebar-anchor',
  ) as HTMLElement

  if (!modebarAnchor || !plotContainerRef.current) return

  requestAnimationFrame(() => {
    try {
      const containerRect = plotContainerRef.current!.getBoundingClientRect()
      const anchorRect = modebarAnchor.getBoundingClientRect()

      const x = anchorRect.left - containerRect.left + offsetX
      const y = anchorRect.top - containerRect.top + offsetY

      modebar.style.position = 'absolute'
      modebar.style.left = `${x}px`
      modebar.style.top = `${y}px`
    } catch (e) {
      // Silently handle positioning errors
    }
  })
}

const useModebarPositioning = (
  plotContainerRef: React.RefObject<HTMLDivElement | null>,
  toolboxRef: React.RefObject<HTMLDivElement | null>,
  offsetX: number,
  offsetY: number,
): void => {
  useEffect(() => {
    let resizeListener: (() => void) | null = null

    const modebarObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const modebarAffected =
          Array.from(mutation.addedNodes).some((node) =>
            (node as Element).classList?.contains('modebar'),
          ) || (mutation.target as Element).classList?.contains('modebar')

        if (modebarAffected) {
          updateModebarPosition(plotContainerRef, toolboxRef, offsetX, offsetY)
        }
      }
    })

    if (plotContainerRef.current) {
      modebarObserver.observe(plotContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      })

      resizeListener = () =>
        updateModebarPosition(plotContainerRef, toolboxRef, offsetX, offsetY)
      window.addEventListener('resize', resizeListener)
    }

    return () => {
      modebarObserver.disconnect()
      if (resizeListener) {
        window.removeEventListener('resize', resizeListener)
      }
    }
  }, [plotContainerRef, toolboxRef, offsetX, offsetY])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      if (plotContainerRef.current && toolboxRef.current) {
        updateModebarPosition(plotContainerRef, toolboxRef, offsetX, offsetY)
      }
    })

    return () => {
      cancelAnimationFrame(frameId)
    }
  })
}

const ModebarAnchor: React.FC = () => (
  <div
    className="modebar-anchor"
    style={{
      display: 'inline-block',
      width: 0,
      height: 0,
      overflow: 'hidden',
    }}
  />
)

const GraphContainerModal: React.FC<GraphContainerModalProps> = ({
  title,
  data,
  layout,
  onClose,
  id,
  helpText,
  aiText,
  aiPlusText,
  AISettings,
  hasAIPlus,
  plotGroupId,
  plotPackageId,
  setReportDocument,
  isNewlyGeneratedReport = false,
  useAIText = true,
  overwriteMessage = null,
  store,
  ...props
}) => {
  const [isAIVisible, setIsAIVisible] = useState<boolean>(true)
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  const plotContainerRef = useRef<HTMLDivElement>(null)
  const toolboxRef = useRef<HTMLDivElement>(null)

  useModebarPositioning(plotContainerRef, toolboxRef, -127, -42)

  const handleHelpOpen = (): void => setShowHelpModal(true)
  const handleHelpClose = (): void => setShowHelpModal(false)

  return (
    <>
      <Modal
        title={title}
        size={'xxl'}
        style={{ minHeight: '70vh', maxHeight: '90vh' }}
        onClose={onClose}
        headerButtons={
          [
            {
              icon: (
                <CustomIcons.QuestionIcon
                  color={theme.colors.fadedText}
                  size="xs"
                />
              ),
              onClick: handleHelpOpen,
              ariaLabel: 'Help',
            },
          ] as HeaderButton[]
        }
        {...props}
      >
        <Modal.Body
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: '20px',
          }}
        >
          <div
            ref={plotContainerRef}
            style={{
              width: '90%',
              height: '50VH',
              paddingTop: '1.5rem',
              paddingBottom: '0.7rem',
              marginBottom: '1rem',
              position: 'relative',
            }}
          >
            <div
              ref={toolboxRef}
              style={{
                position: 'absolute',
                top: '0px',
                right: '0px',
                zIndex: 10,
              }}
            >
              <ModebarAnchor />
            </div>
            {overwriteMessage ? (
              <OverwriteMessageContainer isModal={true}>
                {overwriteMessage}
              </OverwriteMessageContainer>
            ) : (
              <PlotlyComponent
                data={data || []}
                layout={{ ...layout, showlegend: true }}
                config={baseConfig}
                style={{ width: '100%', height: '100%' }}
              />
            )}
          </div>
          {useAIText && (
            <AIHStack>
              <AICheckboxAndContainer>
                <ModalCheckboxContainer>
                  {' '}
                  <OneLineBtnHStack style={{ marginLeft: '1rem' }}>
                    <StandardCheckbox
                      checked={isAIVisible}
                      onChange={(event) => setIsAIVisible(event.target.checked)}
                      size="small"
                    />
                    <span>AI analysis</span>
                  </OneLineBtnHStack>
                  <AIPlusBadge disabled={!hasAIPlus}>AI +</AIPlusBadge>
                </ModalCheckboxContainer>
                {isAIVisible && (
                  <AIEditableContainer
                    title={hasAIPlus ? 'AI+ Description' : 'AI Description'}
                    prompt={`title: ${title}, description: ${helpText}, data: ${JSON.stringify(data)}, context: ${AISettings}, Write a description of the plot based on only values within 150 words; Hint: don't describe the plots, only comment based on the value.`}
                    textSrcType={
                      hasAIPlus
                        ? AITextType.PLOT_DESCRIPTION_PLUS
                        : AITextType.PLOT_DESCRIPTION
                    }
                    plotGroupId={plotGroupId}
                    plotPackageId={plotPackageId}
                    maxHeight={250}
                    style={{ maxWidth: '100%' }}
                    isNewlyGeneratedReport={false}
                    hasAIPlus={hasAIPlus}
                    store={store}
                  />
                )}
              </AICheckboxAndContainer>
            </AIHStack>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.DefaultFooter
            confirmText="Close"
            onConfirm={onClose}
            confirmGhost={true}
            flip
          />
        </Modal.Footer>
      </Modal>

      <HelpModal
        title={title}
        helpText={helpText}
        open={showHelpModal}
        onClose={handleHelpClose}
      />
    </>
  )
}

const HelpModal: React.FC<HelpModalProps> = ({
  title,
  helpText,
  open,
  onClose,
}) => (
  <Modal title={title} open={open} onClose={onClose} size={'md'}>
    <Modal.Body>{helpText}</Modal.Body>
    <Modal.Footer>
      <Modal.DefaultFooter
        confirmText="OK"
        onConfirm={onClose}
        confirmGhost={true}
        flip
      />
    </Modal.Footer>
  </Modal>
)

const GraphContainer: React.FC<GraphContainerProps> = ({
  title,
  hasAIPlus = false,
  AISettings,
  helpText,
  downloadLink,
  aiText,
  aiPlusText,
  size,
  loading = false,
  data,
  layout,
  config,
  plotGroupId,
  plotPackageId,
  setReportDocument,
  useAIText = true,
  isNewlyGeneratedReport = false,
  wide = false,
  overwriteMessage = null,
  store,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  const [isAIVisible, setIsAIVisible] = useState<boolean>(true)
  const plotContainerRef = useRef<HTMLDivElement>(null)
  const toolboxRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{
    width: number
    height: number
  }>({ width: 0, height: 0 })

  const aiEditableSyncRef = useRef<(() => void) | null>(null)
  const aiPlusEditableSyncRef = useRef<(() => void) | null>(null)

  const isLoading = loading || !data || !layout
  const offsetX = wide ? -149 : -132
  const offsetY = wide ? -18 : -18

  useModebarPositioning(plotContainerRef, toolboxRef, offsetX, offsetY)

  const handleExpand = (): void => setShowModal(true)

  const handleClose = (): void => {
    if (aiEditableSyncRef.current) {
      aiEditableSyncRef.current()
    }

    if (aiPlusEditableSyncRef.current) {
      aiPlusEditableSyncRef.current()
    }

    setShowModal(false)
  }

  const handleHelpOpen = (): void => setShowHelpModal(true)
  const handleHelpClose = (): void => setShowHelpModal(false)
  const plotConfig: BaseConfigInterface & Partial<Config> = {
    ...baseConfig,
    responsive: true,
  }

  const formattedData: Plotly.PlotData[] = useMemo(() => {
    if (!data || !data.length) return []

    const GRAPHS_WITH_UNIX_TIMESTAMP = [
      'Overview',
      'Trend Breaks',
      'Gaps',
      'Model fit',
      'Driver impact trends',
      'Contribution over time',
    ]
    const shouldUseDateMode = GRAPHS_WITH_UNIX_TIMESTAMP.includes(title)

    if (!shouldUseDateMode) return data

    return data.map((trace) => {
      const firstX = Array.isArray(trace.x) ? trace.x[0] : undefined
      const isNano = typeof firstX === 'number' && firstX > 1e17

      const xFormatted =
        isNano && Array.isArray(trace.x)
          ? (trace.x as number[]).map((ts) => ts / 1e6) // ns → ms
          : trace.x

      return {
        ...trace,
        type: 'scatter',
        x: xFormatted,
        hovertemplate: isNano
          ? 'Date: %{x|%Y-%m-%d}<br>Value: %{y}<extra></extra>'
          : trace.hovertemplate,
      }
    })
  }, [data, title])

  const plotLayout: Partial<Plotly.Layout> = useMemo(() => {
    if (!data || !data.length) return { ...layout }

    const GRAPHS_WITH_UNIX_TIMESTAMP = [
      'Overview',
      'Trend Breaks',
      'Gaps',
      'Model fit',
      'Driver impact trends',
      'Contribution over time',
    ]
    const shouldUseDateMode = GRAPHS_WITH_UNIX_TIMESTAMP.includes(title)
    if (!shouldUseDateMode) return { ...layout }

    const hasTimestamp = data.some((trace) => {
      const firstX = Array.isArray(trace.x) ? trace.x[0] : undefined
      return typeof firstX === 'number' && firstX > 1e12
    })

    if (!hasTimestamp) return { ...layout }

    return {
      ...layout,
      xaxis: {
        type: 'date',
        // tickformat: '%Y-%m-%d',
        // hoverformat: '%Y-%m-%d',
      },
      xaxis2: { type: 'date', anchor: 'y2' },
      xaxis3: { type: 'date', anchor: 'y3' },
      xaxis4: { type: 'date', anchor: 'y4' },
    }
  }, [data, layout, title])

  const responsiveLayout: Partial<Layout> = {
    ...plotLayout,
    autosize: true,
  }

  useEffect(() => {
    const updateSize = (): void => {
      if (plotContainerRef.current) {
        const rect = plotContainerRef.current.getBoundingClientRect()
        setContainerSize({ width: rect.width, height: rect.height })
      }
    }

    updateSize()

    const resizeObserver = new ResizeObserver(updateSize)
    if (plotContainerRef.current) {
      resizeObserver.observe(plotContainerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    if (plotContainerRef.current && containerSize.width > 0) {
      updateModebarPosition(plotContainerRef, toolboxRef, offsetX, offsetY)
    }
  }, [containerSize, offsetX, offsetY])

  const graphArea = (
    <>
      <HeaderContainer>
        <HeaderTitle>{title}</HeaderTitle>
        <Toolbox ref={toolboxRef}>
          <ModebarAnchor />
          <CustomIcons.ExpandIcon
            color={theme.colors.veryVeryVeryFadedText}
            size="xxs"
            onClick={handleExpand}
          />
          {helpText && (
            <CustomIcons.QuestionIcon
              color={theme.colors.veryVeryVeryFadedText}
              size="xs"
              onClick={handleHelpOpen}
            />
          )}
          {downloadLink && (
            <Whisper
              placement="bottom"
              trigger="hover"
              speaker={<Tooltip>Download chart data as CSV</Tooltip>}
            >
              <span style={{ marginTop: '4px' }}>
                <CustomIcons.faDownload
                  color={theme.colors.veryVeryVeryFadedText}
                  size="xs"
                  onClick={() => {
                    const link = document.createElement('a')
                    link.href = downloadLink
                    link.download = `${title}.csv`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                  }}
                />
              </span>
            </Whisper>
          )}
        </Toolbox>
      </HeaderContainer>

      <GraphPlotArea ref={plotContainerRef} wide={wide}>
        <UniversalLoader show={isLoading} />
        {!isLoading &&
          (overwriteMessage ? (
            <OverwriteMessageContainer wide={wide}>
              {overwriteMessage}
            </OverwriteMessageContainer>
          ) : (
            <PlotlyComponent
              data={formattedData}
              layout={{
                showlegend: title === 'What-if simulation',
                ...responsiveLayout,
              }}
              config={plotConfig}
              useResizeHandler={true}
              style={{
                width: '100%',
                height: wide ? '27rem' : '22rem',
                padding: '0.5rem',
                overflow: 'auto',
              }}
            />
          ))}
      </GraphPlotArea>
    </>
  )
  const aiTextArea = useAIText && (
    <AICheckboxAndContainer>
      <CheckboxContainer wide={wide}>
        <OneLineBtnHStack style={{ marginLeft: wide ? '0rem' : '1rem' }}>
          <StandardCheckbox
            checked={isAIVisible}
            onChange={(event) => setIsAIVisible(event.target.checked)}
            size="small"
          />
          <span>AI analysis</span>
        </OneLineBtnHStack>
        <AIPlusBadge disabled={!hasAIPlus}>AI +</AIPlusBadge>
      </CheckboxContainer>
      {isAIVisible && (
        <AIEditableContainer
          title={hasAIPlus ? 'AI+ Description' : 'AI Description'}
          hasAIPlus={hasAIPlus}
          prompt={`title: ${title}, description: ${helpText}, data: ${JSON.stringify(data)}, context: ${AISettings}, Write a description of the plot based on only values within 150 words; Hint: don't describe the plots, only comment based on the value.`}
          textSrcType={
            hasAIPlus
              ? AITextType.PLOT_DESCRIPTION_PLUS
              : AITextType.PLOT_DESCRIPTION
          }
          plotGroupId={plotGroupId}
          plotPackageId={plotPackageId}
          isNewlyGeneratedReport={isNewlyGeneratedReport}
          maxHeight={wide ? 420 : 250}
          wide={wide}
          store={store}
        />
      )}
    </AICheckboxAndContainer>
  )

  return (
    <>
      {wide ? (
        <WideModeOuterContainer>
          <WideModeContainer>
            <WideModeSplitter
              style={{
                width: '63%',
                paddingRight: '0rem',
                paddingLeft: '0.7rem',
              }}
            >
              {graphArea}
            </WideModeSplitter>
            <WideModeSplitter style={{ width: '37%' }}>
              <HeaderContainer wide={wide} />
              {aiTextArea}
            </WideModeSplitter>
          </WideModeContainer>
        </WideModeOuterContainer>
      ) : (
        <OuterContainer size={size}>
          <Container>
            {graphArea}
            {aiTextArea}
          </Container>
        </OuterContainer>
      )}

      <GraphContainerModal
        title={title}
        data={formattedData}
        layout={responsiveLayout}
        open={showModal}
        onClose={handleClose}
        helpText={helpText}
        aiText={aiText}
        aiPlusText={aiPlusText}
        AISettings={AISettings}
        hasAIPlus={hasAIPlus}
        plotGroupId={plotGroupId}
        plotPackageId={plotPackageId}
        setReportDocument={setReportDocument}
        useAIText={useAIText}
        overwriteMessage={overwriteMessage}
        store={store}
      />

      <HelpModal
        title={title}
        helpText={helpText}
        open={showHelpModal}
        onClose={handleHelpClose}
      />
    </>
  )
}

export default GraphContainer
